/* eslint-disable max-classes-per-file */

import { PayloadActionInterface } from "@app/store/actions/payload-action.interface";
import { ContributorBadge } from "@core/enums/forum/contributor-badge.enum";
import { CategoryInterface } from "@core/interfaces/forums/category.interface";
import {
  CommunityNoteHistoryInterface,
  CommunityNoteInterface,
  CreateCommunityNoteDto,
  UpdateCommunityNoteDto
} from "@core/interfaces/forums/community-note.interface";
import { ForumInterface } from "@core/interfaces/forums/forum.interface";
import { PollType } from "@core/interfaces/forums/poll-type.enum";
import { PostBadgesResponse } from "@core/interfaces/forums/post-badge.interface";
import { PostInterface } from "@core/interfaces/forums/post.interface";
import { TagInterface } from "@core/interfaces/forums/tag.interface";
import { TopicInterface } from "@core/interfaces/forums/topic.interface";
import { Action } from "@ngrx/store";

export enum ForumActionTypes {
  // Categories
  LOAD_CATEGORIES = "[Forum] Load categories",
  LOAD_CATEGORIES_SUCCESS = "[Forum] Load categories success",
  LOAD_CATEGORIES_FAILURE = "[Forum] Load categories failure",

  // Forums
  LOAD_FORUMS = "[Forum] Load forums",
  LOAD_FORUMS_SUCCESS = "[Forum] Load forums success",
  LOAD_FORUMS_FAILURE = "[Forum] Load forums failure",

  // Topics
  LOAD_TOPICS = "[Forum] Load topics",
  LOAD_TOPICS_SUCCESS = "[Forum] Load topics success",
  LOAD_TOPICS_FAILURE = "[Forum] Load topics failure",
  CLEAR_TOPICS = "[Forum] Clear topics",

  // Read status tracking
  MARK_POST_AS_READ = "[Forum] Mark post as read",
  MARK_POST_AS_READ_SUCCESS = "[Forum] Mark post as read success",
  MARK_POST_AS_READ_FAILURE = "[Forum] Mark post as read failure",

  // Posts
  LOAD_POSTS = "[Forum] Load posts",
  LOAD_POSTS_SUCCESS = "[Forum] Load posts success",
  LOAD_POSTS_FAILURE = "[Forum] Load posts failure",
  CLEAR_POSTS = "[Forum] Clear posts",
  CREATE_POST = "[Forum] Create post",
  CREATE_POST_SUCCESS = "[Forum] Create post success",
  CREATE_POST_FAILURE = "[Forum] Create post failure",
  DELETE_POST = "[Forum] Delete post",
  DELETE_POST_SUCCESS = "[Forum] Delete post success",
  DELETE_POST_FAILURE = "[Forum] Delete post failure",
  APPROVE_POST = "[Forum] Approve post",
  APPROVE_POST_SUCCESS = "[Forum] Approve post success",
  APPROVE_POST_FAILURE = "[Forum] Approve post failure",

  // Single item getters
  GET_CATEGORY = "[Forum] Get category",
  GET_CATEGORY_SUCCESS = "[Forum] Get category success",
  GET_CATEGORY_FAILURE = "[Forum] Get category failure",

  GET_FORUM = "[Forum] Get forum",
  GET_FORUM_SUCCESS = "[Forum] Get forum success",
  GET_FORUM_FAILURE = "[Forum] Get forum failure",

  GET_TOPIC = "[Forum] Get topic",
  GET_TOPIC_SUCCESS = "[Forum] Get topic success",
  GET_TOPIC_FAILURE = "[Forum] Get topic failure",

  GET_POST = "[Forum] Get post",
  GET_POST_SUCCESS = "[Forum] Get post success",
  GET_POST_FAILURE = "[Forum] Get post failure",

  // Topic Subscriptions
  SUBSCRIBE_TOPIC = "[Forum] Subscribe to topic",
  SUBSCRIBE_TOPIC_SUCCESS = "[Forum] Subscribe to topic success",
  SUBSCRIBE_TOPIC_FAILURE = "[Forum] Subscribe to topic failure",

  UNSUBSCRIBE_TOPIC = "[Forum] Unsubscribe from topic",
  UNSUBSCRIBE_TOPIC_SUCCESS = "[Forum] Unsubscribe from topic success",
  UNSUBSCRIBE_TOPIC_FAILURE = "[Forum] Unsubscribe from topic failure",

  // Poll Voting
  VOTE_POLL = "[Forum] Vote on poll",
  VOTE_POLL_SUCCESS = "[Forum] Vote on poll success",
  VOTE_POLL_FAILURE = "[Forum] Vote on poll failure",

  CANCEL_POLL_VOTE = "[Forum] Cancel poll vote",
  CANCEL_POLL_VOTE_SUCCESS = "[Forum] Cancel poll vote success",
  CANCEL_POLL_VOTE_FAILURE = "[Forum] Cancel poll vote failure",

  // Contributor Badges
  LOAD_CONTRIBUTOR_BADGES = "[Forum] Load contributor badges",
  LOAD_CONTRIBUTOR_BADGES_SUCCESS = "[Forum] Load contributor badges success",
  LOAD_CONTRIBUTOR_BADGES_FAILURE = "[Forum] Load contributor badges failure",

  // Topic List Badges
  LOAD_TOPIC_LIST_BADGES = "[Forum] Load topic list badges",
  LOAD_TOPIC_LIST_BADGES_SUCCESS = "[Forum] Load topic list badges success",
  LOAD_TOPIC_LIST_BADGES_FAILURE = "[Forum] Load topic list badges failure",

  // Post Badges
  LOAD_POST_BADGES = "[Forum] Load post badges",
  LOAD_POST_BADGES_SUCCESS = "[Forum] Load post badges success",
  LOAD_POST_BADGES_FAILURE = "[Forum] Load post badges failure",

  // Tags
  LOAD_TAGS = "[Forum] Load tags",
  LOAD_TAGS_SUCCESS = "[Forum] Load tags success",
  LOAD_TAGS_FAILURE = "[Forum] Load tags failure",

  // Table of Contents
  EDIT_TOC_ENTRY = "[Forum] Edit ToC entry",
  EDIT_TOC_ENTRY_SUCCESS = "[Forum] Edit ToC entry success",
  EDIT_TOC_ENTRY_FAILURE = "[Forum] Edit ToC entry failure",

  // Topic Tags
  UPDATE_TOPIC_TAGS = "[Forum] Update topic tags",
  UPDATE_TOPIC_TAGS_SUCCESS = "[Forum] Update topic tags success",
  UPDATE_TOPIC_TAGS_FAILURE = "[Forum] Update topic tags failure",

  // Post Likes
  LOAD_POST_LIKES = "[Forum] Load post likes",
  LOAD_POST_LIKES_SUCCESS = "[Forum] Load post likes success",
  LOAD_POST_LIKES_FAILURE = "[Forum] Load post likes failure",

  // Post Content Type
  SET_POST_CONTENT_TYPE = "[Forum] Set post content type",

  // Community Notes
  LOAD_COMMUNITY_NOTES = "[Forum] Load community notes",
  LOAD_COMMUNITY_NOTES_SUCCESS = "[Forum] Load community notes success",
  LOAD_COMMUNITY_NOTES_FAILURE = "[Forum] Load community notes failure",
  CREATE_COMMUNITY_NOTE = "[Forum] Create community note",
  CREATE_COMMUNITY_NOTE_SUCCESS = "[Forum] Create community note success",
  CREATE_COMMUNITY_NOTE_FAILURE = "[Forum] Create community note failure",
  UPDATE_COMMUNITY_NOTE = "[Forum] Update community note",
  UPDATE_COMMUNITY_NOTE_SUCCESS = "[Forum] Update community note success",
  UPDATE_COMMUNITY_NOTE_FAILURE = "[Forum] Update community note failure",
  DELETE_COMMUNITY_NOTE = "[Forum] Delete community note",
  DELETE_COMMUNITY_NOTE_SUCCESS = "[Forum] Delete community note success",
  DELETE_COMMUNITY_NOTE_FAILURE = "[Forum] Delete community note failure",
  UPVOTE_COMMUNITY_NOTE = "[Forum] Upvote community note",
  UPVOTE_COMMUNITY_NOTE_SUCCESS = "[Forum] Upvote community note success",
  UPVOTE_COMMUNITY_NOTE_FAILURE = "[Forum] Upvote community note failure",
  REMOVE_UPVOTE_COMMUNITY_NOTE = "[Forum] Remove upvote community note",
  REMOVE_UPVOTE_COMMUNITY_NOTE_SUCCESS = "[Forum] Remove upvote community note success",
  REMOVE_UPVOTE_COMMUNITY_NOTE_FAILURE = "[Forum] Remove upvote community note failure",
  LOAD_COMMUNITY_NOTE_HISTORY = "[Forum] Load community note history",
  LOAD_COMMUNITY_NOTE_HISTORY_SUCCESS = "[Forum] Load community note history success",
  LOAD_COMMUNITY_NOTE_HISTORY_FAILURE = "[Forum] Load community note history failure",

  // Topic Moderation
  CLOSE_TOPIC = "[Forum] Close topic",
  CLOSE_TOPIC_SUCCESS = "[Forum] Close topic success",
  CLOSE_TOPIC_FAILURE = "[Forum] Close topic failure",
  REOPEN_TOPIC = "[Forum] Reopen topic",
  REOPEN_TOPIC_SUCCESS = "[Forum] Reopen topic success",
  REOPEN_TOPIC_FAILURE = "[Forum] Reopen topic failure",
  MAKE_STICKY_TOPIC = "[Forum] Make sticky topic",
  MAKE_STICKY_TOPIC_SUCCESS = "[Forum] Make sticky topic success",
  MAKE_STICKY_TOPIC_FAILURE = "[Forum] Make sticky topic failure",
  REMOVE_STICKY_TOPIC = "[Forum] Remove sticky topic",
  REMOVE_STICKY_TOPIC_SUCCESS = "[Forum] Remove sticky topic success",
  REMOVE_STICKY_TOPIC_FAILURE = "[Forum] Remove sticky topic failure",
  MOVE_TOPIC = "[Forum] Move topic",
  MOVE_TOPIC_SUCCESS = "[Forum] Move topic success",
  MOVE_TOPIC_FAILURE = "[Forum] Move topic failure"
}

// Categories actions
export class LoadCategories implements Action {
  readonly type = ForumActionTypes.LOAD_CATEGORIES;
}

export class LoadCategoriesSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_CATEGORIES_SUCCESS;

  constructor(public payload: { categories: CategoryInterface[]; total: number }) {}
}

export class LoadCategoriesFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_CATEGORIES_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Forums actions
export class LoadForums implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_FORUMS;

  constructor(
    public payload: { categoryId?: CategoryInterface["id"]; parentForumId?: ForumInterface["id"]; page?: number }
  ) {}
}

export class LoadForumsSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_FORUMS_SUCCESS;

  constructor(public payload: { forums: ForumInterface[]; total: number }) {}
}

export class LoadForumsFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_FORUMS_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Topics actions
export class LoadTopics implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_TOPICS;

  constructor(public payload: { forumId?: ForumInterface["id"]; page?: number }) {}
}

export class LoadTopicsSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_TOPICS_SUCCESS;

  constructor(public payload: { topics: TopicInterface[]; total: number; hasNext: boolean }) {}
}

export class LoadTopicsFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_TOPICS_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class ClearTopics implements Action {
  readonly type = ForumActionTypes.CLEAR_TOPICS;
}

// Active Topics actions
// Posts actions
export class LoadPosts implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_POSTS;

  constructor(public payload: { topicId: TopicInterface["id"]; page?: number }) {}
}

export class LoadPostsSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_POSTS_SUCCESS;

  constructor(public payload: { posts: PostInterface[]; total: number; hasNext?: boolean }) {}
}

export class LoadPostsFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_POSTS_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class ClearPosts implements Action {
  readonly type = ForumActionTypes.CLEAR_POSTS;
}

// Create post actions
export class CreatePost implements PayloadActionInterface {
  readonly type = ForumActionTypes.CREATE_POST;

  constructor(
    public payload: {
      body: string;
      bodyHtml?: string;
      topicId?: TopicInterface["id"];
      topicName?: string;
      tagSlugs?: string[];
      forumId?: ForumInterface["id"];
      pollType?: PollType;
      pollQuestion?: string;
      pollAnswers?: string[];
    }
  ) {}
}

export class CreatePostSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.CREATE_POST_SUCCESS;

  constructor(public payload: { post: PostInterface }) {}
}

export class CreatePostFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.CREATE_POST_FAILURE;

  constructor(public payload: { error: any }) {}
}

// Delete post actions
export class DeletePost implements PayloadActionInterface {
  readonly type = ForumActionTypes.DELETE_POST;

  constructor(public payload: { postId: PostInterface["id"] }) {}
}

export class DeletePostSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.DELETE_POST_SUCCESS;

  constructor(public payload: { postId: PostInterface["id"] }) {}
}

export class DeletePostFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.DELETE_POST_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Single item getters
export class GetCategory implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_CATEGORY;

  constructor(public payload: { categoryId: CategoryInterface["id"] }) {}
}

export class GetCategorySuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_CATEGORY_SUCCESS;

  constructor(public payload: { category: CategoryInterface }) {}
}

export class GetCategoryFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_CATEGORY_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class GetForum implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_FORUM;

  constructor(public payload: { forumId: ForumInterface["id"] }) {}
}

export class GetForumSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_FORUM_SUCCESS;

  constructor(public payload: { forum: ForumInterface }) {}
}

export class GetForumFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_FORUM_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class GetTopic implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_TOPIC;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class GetTopicSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_TOPIC_SUCCESS;

  constructor(public payload: { topic: TopicInterface }) {}
}

export class GetTopicFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_TOPIC_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class GetPost implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_POST;

  constructor(public payload: { postId: PostInterface["id"] }) {}
}

export class GetPostSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_POST_SUCCESS;

  constructor(public payload: { post: PostInterface }) {}
}

export class GetPostFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.GET_POST_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Contributor Badges actions
export class LoadContributorBadges implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_CONTRIBUTOR_BADGES;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class LoadContributorBadgesSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_CONTRIBUTOR_BADGES_SUCCESS;

  constructor(public payload: { topicId: number; badges: Record<string, ContributorBadge[]> }) {}
}

export class LoadContributorBadgesFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_CONTRIBUTOR_BADGES_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Topic List Badges actions
export class LoadTopicListBadges implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_TOPIC_LIST_BADGES;

  constructor(public payload: { topicIds: TopicInterface["id"][] }) {}
}

export class LoadTopicListBadgesSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_TOPIC_LIST_BADGES_SUCCESS;

  constructor(public payload: { badges: Record<string, { userId: string; badges: ContributorBadge[] }> }) {}
}

export class LoadTopicListBadgesFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_TOPIC_LIST_BADGES_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Post Badges actions
export class LoadPostBadges implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_POST_BADGES;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class LoadPostBadgesSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_POST_BADGES_SUCCESS;

  constructor(public payload: { topicId: TopicInterface["id"]; badges: PostBadgesResponse }) {}
}

export class LoadPostBadgesFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_POST_BADGES_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Topic Subscription actions
export class SubscribeTopic implements PayloadActionInterface {
  readonly type = ForumActionTypes.SUBSCRIBE_TOPIC;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class SubscribeTopicSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.SUBSCRIBE_TOPIC_SUCCESS;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class SubscribeTopicFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.SUBSCRIBE_TOPIC_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class UnsubscribeTopic implements PayloadActionInterface {
  readonly type = ForumActionTypes.UNSUBSCRIBE_TOPIC;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class UnsubscribeTopicSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.UNSUBSCRIBE_TOPIC_SUCCESS;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class UnsubscribeTopicFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.UNSUBSCRIBE_TOPIC_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Poll Voting actions
export class VotePoll implements PayloadActionInterface {
  readonly type = ForumActionTypes.VOTE_POLL;

  constructor(
    public payload: {
      topicId: TopicInterface["id"];
      pollType: PollType;
      answerIds: number | number[];
    }
  ) {}
}

export class VotePollSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.VOTE_POLL_SUCCESS;

  constructor(public payload: { topic: TopicInterface }) {}
}

export class VotePollFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.VOTE_POLL_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Cancel Poll Vote actions
export class CancelPollVote implements PayloadActionInterface {
  readonly type = ForumActionTypes.CANCEL_POLL_VOTE;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class CancelPollVoteSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.CANCEL_POLL_VOTE_SUCCESS;

  constructor(public payload: { topic: TopicInterface }) {}
}

export class CancelPollVoteFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.CANCEL_POLL_VOTE_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class MarkPostAsRead implements PayloadActionInterface {
  readonly type = ForumActionTypes.MARK_POST_AS_READ;

  constructor(public payload: { topicId: number; postId: number }) {}
}

export class MarkPostAsReadSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.MARK_POST_AS_READ_SUCCESS;

  constructor(public payload: { topic: TopicInterface }) {}
}

export class MarkPostAsReadFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.MARK_POST_AS_READ_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Approve post actions
export class ApprovePost implements PayloadActionInterface {
  readonly type = ForumActionTypes.APPROVE_POST;

  constructor(public payload: { postId: PostInterface["id"] }) {}
}

export class ApprovePostSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.APPROVE_POST_SUCCESS;

  constructor(public payload: { post: PostInterface }) {}
}

export class ApprovePostFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.APPROVE_POST_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Tags actions
export class LoadTags implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_TAGS;

  constructor(
    public payload: {
      page?: number;
      search?: string;
      followed?: boolean;
      ordering?: string;
      excludeCategories?: string;
      category?: string;
      pageSize?: number;
      append?: boolean; // Whether to append to existing tags or replace
    }
  ) {}
}

export class LoadTagsSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_TAGS_SUCCESS;

  constructor(public payload: { tags: TagInterface[]; total: number; append?: boolean }) {}
}

export class LoadTagsFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_TAGS_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Table of Contents actions
export class EditTocEntry implements PayloadActionInterface {
  readonly type = ForumActionTypes.EDIT_TOC_ENTRY;

  constructor(
    public payload: {
      topicId: number;
      entryId: number;
      summary: string;
      editReason?: string;
    }
  ) {}
}

export class EditTocEntrySuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.EDIT_TOC_ENTRY_SUCCESS;

  constructor(
    public payload: {
      topicId: number;
      entryId: number;
      updatedEntry: any; // ForumTocEntry from v2 API
    }
  ) {}
}

export class EditTocEntryFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.EDIT_TOC_ENTRY_FAILURE;

  constructor(public payload: { error: any }) {}
}

// Topic Tags actions
export class UpdateTopicTags implements PayloadActionInterface {
  readonly type = ForumActionTypes.UPDATE_TOPIC_TAGS;

  constructor(public payload: { topicId: number; tagSlugs: string[] }) {}
}

export class UpdateTopicTagsSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.UPDATE_TOPIC_TAGS_SUCCESS;

  constructor(public payload: { topic: TopicInterface }) {}
}

export class UpdateTopicTagsFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.UPDATE_TOPIC_TAGS_FAILURE;

  constructor(public payload: { error: any }) {}
}

// Post Likes actions
export class LoadPostLikes implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_POST_LIKES;

  constructor(public payload: { postIds: PostInterface["id"][]; contentType: number; currentUserId: number | null }) {}
}

export class LoadPostLikesSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_POST_LIKES_SUCCESS;

  constructor(
    public payload: {
      likeCounts: Record<PostInterface["id"], number>;
      userLikedPosts: PostInterface["id"][];
    }
  ) {}
}

export class LoadPostLikesFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_POST_LIKES_FAILURE;

  constructor(public payload: { error: string }) {}
}

// Set Post Content Type action
export class SetPostContentType implements PayloadActionInterface {
  readonly type = ForumActionTypes.SET_POST_CONTENT_TYPE;

  constructor(public payload: { contentTypeId: number }) {}
}

// Community Notes actions
export class LoadCommunityNotes implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_COMMUNITY_NOTES;

  constructor(public payload: { postId: number }) {}
}

export class LoadCommunityNotesSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_COMMUNITY_NOTES_SUCCESS;

  constructor(public payload: { postId: number; notes: CommunityNoteInterface[] }) {}
}

export class LoadCommunityNotesFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_COMMUNITY_NOTES_FAILURE;

  constructor(public payload: { postId: number; error: string }) {}
}

export class CreateCommunityNote implements PayloadActionInterface {
  readonly type = ForumActionTypes.CREATE_COMMUNITY_NOTE;

  constructor(public payload: { postId: number; note: CreateCommunityNoteDto }) {}
}

export class CreateCommunityNoteSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.CREATE_COMMUNITY_NOTE_SUCCESS;

  constructor(public payload: { note: CommunityNoteInterface }) {}
}

export class CreateCommunityNoteFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.CREATE_COMMUNITY_NOTE_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class UpdateCommunityNote implements PayloadActionInterface {
  readonly type = ForumActionTypes.UPDATE_COMMUNITY_NOTE;

  constructor(public payload: { noteId: number; updates: UpdateCommunityNoteDto }) {}
}

export class UpdateCommunityNoteSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.UPDATE_COMMUNITY_NOTE_SUCCESS;

  constructor(public payload: { note: CommunityNoteInterface }) {}
}

export class UpdateCommunityNoteFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.UPDATE_COMMUNITY_NOTE_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class DeleteCommunityNote implements PayloadActionInterface {
  readonly type = ForumActionTypes.DELETE_COMMUNITY_NOTE;

  constructor(public payload: { noteId: number }) {}
}

export class DeleteCommunityNoteSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.DELETE_COMMUNITY_NOTE_SUCCESS;

  constructor(public payload: { noteId: number }) {}
}

export class DeleteCommunityNoteFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.DELETE_COMMUNITY_NOTE_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class UpvoteCommunityNote implements PayloadActionInterface {
  readonly type = ForumActionTypes.UPVOTE_COMMUNITY_NOTE;

  constructor(public payload: { noteId: number }) {}
}

export class UpvoteCommunityNoteSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.UPVOTE_COMMUNITY_NOTE_SUCCESS;

  constructor(public payload: { noteId: number }) {}
}

export class UpvoteCommunityNoteFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.UPVOTE_COMMUNITY_NOTE_FAILURE;

  constructor(public payload: { noteId: number; error: string }) {}
}

export class RemoveUpvoteCommunityNote implements PayloadActionInterface {
  readonly type = ForumActionTypes.REMOVE_UPVOTE_COMMUNITY_NOTE;

  constructor(public payload: { noteId: number }) {}
}

export class RemoveUpvoteCommunityNoteSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.REMOVE_UPVOTE_COMMUNITY_NOTE_SUCCESS;

  constructor(public payload: { noteId: number }) {}
}

export class RemoveUpvoteCommunityNoteFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.REMOVE_UPVOTE_COMMUNITY_NOTE_FAILURE;

  constructor(public payload: { noteId: number; error: string }) {}
}

export class LoadCommunityNoteHistory implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_COMMUNITY_NOTE_HISTORY;

  constructor(public payload: { noteId: number }) {}
}

export class LoadCommunityNoteHistorySuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_COMMUNITY_NOTE_HISTORY_SUCCESS;

  constructor(public payload: { history: CommunityNoteHistoryInterface }) {}
}

export class LoadCommunityNoteHistoryFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.LOAD_COMMUNITY_NOTE_HISTORY_FAILURE;

  constructor(public payload: { noteId: number; error: string }) {}
}

// Topic Moderation actions
export class CloseTopic implements PayloadActionInterface {
  readonly type = ForumActionTypes.CLOSE_TOPIC;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class CloseTopicSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.CLOSE_TOPIC_SUCCESS;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class CloseTopicFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.CLOSE_TOPIC_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class ReopenTopic implements PayloadActionInterface {
  readonly type = ForumActionTypes.REOPEN_TOPIC;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class ReopenTopicSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.REOPEN_TOPIC_SUCCESS;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class ReopenTopicFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.REOPEN_TOPIC_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class MakeStickyTopic implements PayloadActionInterface {
  readonly type = ForumActionTypes.MAKE_STICKY_TOPIC;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class MakeStickyTopicSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.MAKE_STICKY_TOPIC_SUCCESS;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class MakeStickyTopicFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.MAKE_STICKY_TOPIC_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class RemoveStickyTopic implements PayloadActionInterface {
  readonly type = ForumActionTypes.REMOVE_STICKY_TOPIC;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class RemoveStickyTopicSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.REMOVE_STICKY_TOPIC_SUCCESS;

  constructor(public payload: { topicId: TopicInterface["id"] }) {}
}

export class RemoveStickyTopicFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.REMOVE_STICKY_TOPIC_FAILURE;

  constructor(public payload: { error: string }) {}
}

export class MoveTopic implements PayloadActionInterface {
  readonly type = ForumActionTypes.MOVE_TOPIC;

  constructor(public payload: { topicId: TopicInterface["id"]; forumId: ForumInterface["id"] }) {}
}

export class MoveTopicSuccess implements PayloadActionInterface {
  readonly type = ForumActionTypes.MOVE_TOPIC_SUCCESS;

  constructor(
    public payload: {
      topicId: TopicInterface["id"];
      newForumId: number;
      newForumName: string;
      newSlug: string;
    }
  ) {}
}

export class MoveTopicFailure implements PayloadActionInterface {
  readonly type = ForumActionTypes.MOVE_TOPIC_FAILURE;

  constructor(public payload: { error: string }) {}
}

export type All =
  | LoadCategories
  | LoadCategoriesSuccess
  | LoadCategoriesFailure
  | LoadForums
  | LoadForumsSuccess
  | LoadForumsFailure
  | LoadTopics
  | LoadTopicsSuccess
  | LoadTopicsFailure
  | ClearTopics
  | LoadPosts
  | LoadPostsSuccess
  | LoadPostsFailure
  | ClearPosts
  | CreatePost
  | CreatePostSuccess
  | CreatePostFailure
  | DeletePost
  | DeletePostSuccess
  | DeletePostFailure
  | ApprovePost
  | ApprovePostSuccess
  | ApprovePostFailure
  | GetCategory
  | GetCategorySuccess
  | GetCategoryFailure
  | GetForum
  | GetForumSuccess
  | GetForumFailure
  | GetTopic
  | GetTopicSuccess
  | GetTopicFailure
  | GetPost
  | GetPostSuccess
  | GetPostFailure
  | SubscribeTopic
  | SubscribeTopicSuccess
  | SubscribeTopicFailure
  | UnsubscribeTopic
  | UnsubscribeTopicSuccess
  | UnsubscribeTopicFailure
  | VotePoll
  | VotePollSuccess
  | VotePollFailure
  | CancelPollVote
  | CancelPollVoteSuccess
  | CancelPollVoteFailure
  | LoadContributorBadges
  | LoadContributorBadgesSuccess
  | LoadContributorBadgesFailure
  | LoadTopicListBadges
  | LoadTopicListBadgesSuccess
  | LoadTopicListBadgesFailure
  | LoadPostBadges
  | LoadPostBadgesSuccess
  | LoadPostBadgesFailure
  | MarkPostAsRead
  | MarkPostAsReadSuccess
  | MarkPostAsReadFailure
  | LoadTags
  | LoadTagsSuccess
  | LoadTagsFailure
  | EditTocEntry
  | EditTocEntrySuccess
  | EditTocEntryFailure
  | UpdateTopicTags
  | UpdateTopicTagsSuccess
  | UpdateTopicTagsFailure
  | LoadPostLikes
  | LoadPostLikesSuccess
  | LoadPostLikesFailure
  | SetPostContentType
  | LoadCommunityNotes
  | LoadCommunityNotesSuccess
  | LoadCommunityNotesFailure
  | CreateCommunityNote
  | CreateCommunityNoteSuccess
  | CreateCommunityNoteFailure
  | UpdateCommunityNote
  | UpdateCommunityNoteSuccess
  | UpdateCommunityNoteFailure
  | DeleteCommunityNote
  | DeleteCommunityNoteSuccess
  | DeleteCommunityNoteFailure
  | UpvoteCommunityNote
  | UpvoteCommunityNoteSuccess
  | UpvoteCommunityNoteFailure
  | RemoveUpvoteCommunityNote
  | RemoveUpvoteCommunityNoteSuccess
  | RemoveUpvoteCommunityNoteFailure
  | LoadCommunityNoteHistory
  | LoadCommunityNoteHistorySuccess
  | LoadCommunityNoteHistoryFailure
  | CloseTopic
  | CloseTopicSuccess
  | CloseTopicFailure
  | ReopenTopic
  | ReopenTopicSuccess
  | ReopenTopicFailure
  | MakeStickyTopic
  | MakeStickyTopicSuccess
  | MakeStickyTopicFailure
  | RemoveStickyTopic
  | RemoveStickyTopicSuccess
  | RemoveStickyTopicFailure
  | MoveTopic
  | MoveTopicSuccess
  | MoveTopicFailure;
